import { z } from "zod";

// Auth validation schemas
export const signUpSchema = z.object({
  email: z.string().email("Invalid email address").max(255, "Email too long"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[0-9]/, "Password must contain at least one number")
    .max(128, "Password too long"),
  captchaToken: z.string().min(1, "Please complete the captcha"),
});

export const signInSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
  captchaToken: z.string().min(1, "Please complete the captcha"),
});

// Dashboard validation schemas
export const packageSubmissionSchema = z.object({
  retailer: z.string().min(1, "Retailer is required").max(100),
  size: z.enum(["small", "medium", "large"], {
    errorMap: () => ({ message: "Invalid package size" }),
  }),
  locationCode: z.string().regex(/^[KUFSW]$/, "Location code must be K, U, F, S, or W"),
  notes: z.string().max(200, "Notes cannot exceed 200 characters").optional(),
  gatedAccess: z.boolean(),
  accessInstructions: z.string().max(200, "Instructions cannot exceed 200 characters").optional(),
  printQrNeeded: z.boolean(),
});

export const fileUploadSchema = z.object({
  file: z.instanceof(File, { message: "File is required" }),
  maxSize: z.number().default(5 * 1024 * 1024), // 5MB
  allowedTypes: z.array(z.string()).default(["image/jpeg", "image/png", "image/jpg", "image/webp"]),
});

export function validateFileUpload(file: File, maxSize = 5 * 1024 * 1024): { valid: boolean; error?: string } {
  const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
  
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: "Only JPEG, PNG, and WebP images are allowed" };
  }
  
  if (file.size > maxSize) {
    return { valid: false, error: `File size must be less than ${maxSize / 1024 / 1024}MB` };
  }
  
  return { valid: true };
}

// Settings validation schemas
export const profileUpdateSchema = z.object({
  phone_number: z.string().max(20, "Phone number too long").optional(),
  street: z.string().max(200, "Street address too long").optional(),
  city: z.string().max(100, "City name too long").optional(),
  zip: z.string().regex(/^\d{5}(-\d{4})?$/, "Invalid ZIP code format").optional(),
  state: z.string().length(2, "State must be 2 characters").optional(),
  county: z.string().max(50, "County name too long").optional(),
  gate_code: z.string().max(200, "Gate code/instructions too long").optional(),
  payment_method: z.string().max(100).optional(),
});
